import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Alert, Button, Card, FormControl } from "react-bootstrap";
import { defineMessages, FormattedMessage } from "react-intl";
import Selector from "../../components/selector";
import { SectionHeading } from "../../components/section-heading";
import { ACCESS_DENIED, getPropertyRpt } from "../../lib/auth";
import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";
import { useHttpClient } from "../../lib/http";
import { useConfig } from "../../lib/config";
import { sendUpsellEmail, getAvailableSelling } from "./infrastructure/sellingConfiguration.api";
import { FullWidthLoader } from "../../components/full-width-loader";
import { useKeycloakAuthz } from "../../components/keycloak-provider";
import Highlight from "react-highlight";

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    title: {
      id: "internalmenu.utilities.upgradeManager",
      defaultMessage: "Upgrade Manager",
    },
    propertyTickerSelectPlaceholder: {
      id: "channelManager.placeholder.propertyTicker",
      defaultMessage: "Engine identifier",
    },
    roomStayId: {
      id: "reservationSellManager.input.roomStayId",
      defaultMessage: "Room stay Id",
    },
    sendUpsellEmail: {
      id: "reservationSellManager.button.sendUpsellEmail",
      defaultMessage: "Send Upsell Email",
    },
    getAvailableSelling: {
      id: "reservationSellManager.button.getAvailableSelling",
      defaultMessage: "Get Available Selling",
    },
    emailList: {
      id: "reservationSellManager.button.emailList",
      defaultMessage: "Email List",
    },
    success: {
      id: "app.success",
      defaultMessage: "Success",
    },
  }),
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem;
  gap: 16px;
`;

const Label = styled.div`
  font-size: 18px;
  margin-bottom: -10px;
`;

const ReservationIdInput = styled(FormControl)``;

const StyledHighlight = styled(Highlight)`
  font-size: 12px;
`;

const HighlightContainer = styled.div`
  display: flex;
  justify-content: center;
  background-color: #f7f7f7;
  padding: 20px;
`;

type Option = { value: string; label: string };

interface OperationError {
  message: string;
}

function UpgradeManager() {
  const userPropertyTickersList = useUserPropertyTickers();
  const config = useConfig();
  const httpClient = useHttpClient(config.sellingApiEndpoint);
  const keycloakAuthz = useKeycloakAuthz();

  const [availableSelling, setAvailableSelling] = useState<Record<string, any> | undefined>(
    undefined
  );
  const [emailList, setEmailList] = useState<string | undefined>(undefined);
  const [roomStayId, setRoomStayId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [configSuccess, setConfigSuccess] = useState<boolean>(false);

  const [propertyTickerOptions, setPropertyTickerOptions] = useState<Option[]>([]);
  const [propertyTickerOption, setPropertyTickerOption] = useState<Option | null | undefined>(
    undefined
  );
  const handleReservationIdChange = useCallback(event => setRoomStayId(event.target.value), [
    setRoomStayId,
  ]);
  const handleEmailListChange = useCallback(event => setEmailList(event.target.value), []);

  useEffect(() => {
    setPropertyTickerOptions(
      userPropertyTickersList.map(option => ({ value: option, label: option }))
    );
  }, [userPropertyTickersList]);

  const handleSelectPropertyTicker = useCallback(
    selectedOption => setPropertyTickerOption(selectedOption),
    [setPropertyTickerOption]
  );

  const handleSendUpsellEmail = useCallback(async () => {
    if (!propertyTickerOption?.value || !roomStayId || !emailList) {
      return;
    }
    setLoading(true);
    setError(undefined);
    setConfigSuccess(false);
    setAvailableSelling(undefined);
    try {
      const rpt = await getPropertyRpt(propertyTickerOption.value, keycloakAuthz);
      await sendUpsellEmail(
        httpClient,
        propertyTickerOption.value,
        roomStayId,
        emailList.split(","),
        {
          headers: {
            authorization: `Bearer ${rpt}`,
          },
        }
      );
      console.log("sendUpsellEmail DONE");
      setConfigSuccess(true);
    } catch (e) {
      setConfigSuccess(false);
      if (e === ACCESS_DENIED) {
        console.error("Access denied", e);
        setError({ message: "Access denied" });
      } else {
        console.error("Error sending upsell email", e);
        setError({ message: "Error sending upsell email" });
      }
    }
    setLoading(false);
  }, [emailList, httpClient, keycloakAuthz, propertyTickerOption?.value, roomStayId]);

  const handleGetAvailableSelling = useCallback(async () => {
    if (!propertyTickerOption?.value || !roomStayId) {
      return;
    }
    setLoading(true);
    setError(undefined);
    setConfigSuccess(false);
    setAvailableSelling(undefined);
    try {
      const rpt = await getPropertyRpt(propertyTickerOption.value, keycloakAuthz);
      const availableSelling = await getAvailableSelling(
        httpClient,
        propertyTickerOption.value,
        roomStayId,
        {
          headers: {
            authorization: `Bearer ${rpt}`,
          },
        }
      );
      delete availableSelling.propertyAri;
      setAvailableSelling(availableSelling);
      console.log("GetAvailableSelling DONE");
      setConfigSuccess(true);
    } catch (e) {
      setConfigSuccess(false);
      if (e === ACCESS_DENIED) {
        console.error("Access denied", e);
        setError({ message: "Access denied" });
      } else {
        console.error("Error sending upsell email", e);
        setError({ message: "Error sending upsell email" });
      }
    }
    setLoading(false);
  }, [httpClient, keycloakAuthz, propertyTickerOption?.value, roomStayId]);

  if (loading) {
    return <FullWidthLoader />;
  }
  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Container>
          <Label>
            <FormattedMessage {...messages.propertyTickerSelectPlaceholder} />
          </Label>
          <Selector
            options={propertyTickerOptions}
            value={propertyTickerOption}
            onChange={handleSelectPropertyTicker}
            placeholder={<FormattedMessage {...messages.propertyTickerSelectPlaceholder} />}
          />
          <Label>
            <FormattedMessage {...messages.roomStayId} />
          </Label>
          <ReservationIdInput
            type="text"
            placeholder="PXXXXXX"
            onChange={handleReservationIdChange}
          />
          <Label>
            <FormattedMessage {...messages.emailList} />
          </Label>
          <FormControl
            type="text"
            placeholder="test@witbooking.com"
            onChange={handleEmailListChange}
          />
        </Container>
        {error && (
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
            </strong>
          </Alert>
        )}
        {configSuccess && (
          <Alert variant="success">
            <strong>
              <FormattedMessage {...messages.success} />
            </strong>
          </Alert>
        )}

        <ButtonContainer>
          <Button variant="primary" type="button" onClick={handleSendUpsellEmail}>
            <FormattedMessage {...messages.sendUpsellEmail} />
          </Button>
          <Button variant="primary" type="button" onClick={handleGetAvailableSelling}>
            <FormattedMessage {...messages.getAvailableSelling} />
          </Button>
        </ButtonContainer>
        {availableSelling ? (
          <div>
            <Button>
              <a
                style={{
                  color: "white",
                  textDecoration: "none",
                }}
                target="_blank"
                href={`${config.engineBaseUrl}/hotel/${
                  propertyTickerOption?.value
                }/upgrade?upgradeToken=${encodeURIComponent(availableSelling.upsellingToken)}`}
                rel="noreferrer">
                Upgrade link
              </a>
            </Button>
            <HighlightContainer>
              <StyledHighlight className="json">
                {JSON.stringify(availableSelling, null, 2)}
              </StyledHighlight>
            </HighlightContainer>
          </div>
        ) : null}
      </Card>
    </>
  );
}

export default withAuthorizationVerifier(UpgradeManager);
